/**
 * Meeting（会议助手）types.
 *
 * Field shapes mirror backend MeetingResponse / MeetingCreate / MeetingUpdate
 * schemas (app/modules/jerry/schemas.py). The API layer camel-cases JSON, so
 * interfaces here use camelCase. String values for source / status /
 * transcriptStatus stay aligned with backend enums
 * (app/modules/jerry/enums.py: MeetingSource / MeetingStatus / TranscriptStatus).
 */

/** Origin platform of a meeting (MeetingSource). `manual` covers hand-created. */
export type MeetingSource =
  | "tencent"
  | "dingtalk"
  | "feishu"
  | "zoom"
  | "manual";

/** Lifecycle (MeetingStatus). */
export type MeetingStatus =
  | "upcoming"
  | "in_progress"
  | "ended"
  | "cancelled";

/** Transcript state (TranscriptStatus). */
export type TranscriptStatus = "none" | "pending" | "ready" | "failed";

/**
 * 处理阶段（MeetingProcessingStage）——这场会整体处理到哪一步了。
 *
 * 派生自转录态、三项 AI 产出态、本人确认与归档等既有正交状态，供列表页给出
 * 一个总览标签与筛选维度，不是新的独立状态机；具体派生规则由后端给出。
 */
export type MeetingProcessingStage =
  | "pending_upload"
  | "transcribing"
  | "analyzing"
  | "pending_analysis"
  | "pending_review"
  | "confirmed"
  | "archived"
  | "failed";

/** 处理阶段的中文标签。 */
export const MEETING_PROCESSING_STAGE_LABELS: Record<MeetingProcessingStage, string> = {
  pending_upload: "待上传",
  transcribing: "转写中",
  analyzing: "分析中",
  pending_analysis: "待分析",
  pending_review: "待审核",
  confirmed: "已确认",
  archived: "已归档",
  failed: "失败",
};

/** 处理阶段标签的 Tag 颜色。 */
export const MEETING_PROCESSING_STAGE_COLORS: Record<MeetingProcessingStage, string> = {
  pending_upload: "default",
  transcribing: "gold",
  analyzing: "gold",
  pending_analysis: "cyan",
  pending_review: "orange",
  confirmed: "blue",
  archived: "green",
  failed: "red",
};

/**
 * 左栏处理阶段快速筛选项：全部 + 五个关键阶段。
 *
 * `pending_upload`/`transcribing`/`analyzing` 三个准备态没有对应的快速筛选
 * 项——它们数量少且转瞬即逝，不构成律师需要主动定位的积压。`pending_analysis`
 * （转写已完成、分析尚未开始）单独列出，因为它代表一批「可以立即行动」的会议
 * 积压，与仍在自动流转、无需人工介入的准备态性质不同。
 */
export const MEETING_PROCESSING_STAGE_QUICK_FILTERS: {
  value: MeetingProcessingStage | undefined;
  label: string;
}[] = [
  { value: undefined, label: "全部" },
  { value: "pending_analysis", label: "待分析" },
  { value: "pending_review", label: "待审核" },
  { value: "confirmed", label: "已确认" },
  { value: "archived", label: "已归档" },
  { value: "failed", label: "失败" },
];

/**
 * 导入态（MeetingImportState）——这一场会在本系统里有多少内容。
 *
 * `listed` 表示只有从平台同步来的清单信息（标题、时间、组织者），纪要尚未拉取；
 * `imported` 表示律师已把内容导入。与生命周期、转录态、产出态互相独立。
 */
export type MeetingImportState = "listed" | "imported";

/** 导入态的中文标签。 */
export const MEETING_IMPORT_STATE_LABELS: Record<MeetingImportState, string> = {
  listed: "未导入内容",
  imported: "已导入内容",
};

/**
 * 转录来源（TranscriptSource）——决定纪要拉不下来时该做什么。
 *
 * `platform_minutes` 向会议平台拉取智能纪要；`local_asr` 本地语音转写；
 * `manual_paste` 律师粘贴纪要正文。
 */
export type TranscriptSource = "platform_minutes" | "local_asr" | "manual_paste";

/** 转录来源的中文标签。 */
export const TRANSCRIPT_SOURCE_LABELS: Record<TranscriptSource, string> = {
  platform_minutes: "平台纪要",
  local_asr: "语音转写",
  manual_paste: "手工粘贴",
};

/**
 * 三项 AI 产出各自的状态（MeetingAnalysisState）。
 *
 * 三项状态互相独立：一项失败不影响另外两项的呈现与提交。
 */
export type MeetingAnalysisState = "none" | "pending" | "ready" | "failed";

/** 产出状态的中文标签。 */
export const MEETING_ANALYSIS_STATE_LABELS: Record<MeetingAnalysisState, string> = {
  none: "未生成",
  pending: "生成中",
  ready: "已就绪",
  failed: "本次未完成",
};

/**
 * 会议的三个 AI 动作标识，取值即接口路径段
 * （`POST/GET /meetings/{id}/actions/{action}`）。
 */
export type MeetingAiAction = "summarize" | "extract-tasks" | "prepare";

/** 三个动作的中文名称，用于按钮、状态提示与失败原因的前缀。 */
export const MEETING_AI_ACTION_LABELS: Record<MeetingAiAction, string> = {
  summarize: "会后总结",
  "extract-tasks": "待办抽取",
  prepare: "会前准备",
};

/** 一个动作的产出状态与结果（GET /meetings/{id}/actions/{action} 的 data）。 */
export interface MeetingAiActionState {
  action: MeetingAiAction;
  state: MeetingAnalysisState;
  /** 本次产出完成时刻；未完成为 null。 */
  completedAt: string | null;
  /** 失败原因，面向律师的可读中文；非失败态为 null。 */
  errorMessage: string | null;
  taskId: string | null;
  /**
   * 只在 `state === "ready"` 时有值。失败与超时不写结果——因此界面上「上一次的
   * 产出」要从会议本身（summary / prepChecklist）与候选待办列表读取，而不是这里。
   */
  result: unknown;
}

/** 提交一次 AI 产出后的即时返回（POST 202 的 data）。 */
export interface MeetingAiActionSubmitResult {
  action: MeetingAiAction;
  state: MeetingAnalysisState;
  meetingId: string;
}

/** OAuth binding state (MeetingConnectionStatus). pending = 设备授权等待用户浏览器确认。 */
export type MeetingConnectionStatus = "pending" | "active" | "expired" | "revoked";

/**
 * Nature of a meeting (MeetingType).
 *
 * `source` describes WHERE the meeting came from (tencent/manual/...);
 * `meetingType` describes WHAT KIND of meeting it is for the law firm
 * (client consultation, court hearing, ...). Recorder-pen captures
 * typically use `client_consultation` or `deposition`.
 */
export type MeetingType =
  | "client_consultation"
  | "internal"
  | "court_hearing"
  | "deposition"
  | "phone_call"
  | "other";

/** Chinese display labels for meeting types. */
export const MEETING_TYPE_LABELS: Record<MeetingType, string> = {
  client_consultation: "客户会谈",
  internal: "内部会议",
  court_hearing: "庭审旁听",
  deposition: "录取证言",
  phone_call: "电话沟通",
  other: "其他",
};

/** Selectable meeting-type options for filters / forms. */
export const MEETING_TYPE_OPTIONS: { value: MeetingType; label: string }[] = (
  Object.entries(MEETING_TYPE_LABELS) as [MeetingType, string][]
).map(([value, label]) => ({ value, label }));

/** Chinese display labels for sources. */
export const MEETING_SOURCE_LABELS: Record<MeetingSource, string> = {
  tencent: "腾讯会议",
  dingtalk: "钉钉会议",
  feishu: "飞书会议",
  zoom: "Zoom",
  manual: "手动创建",
};

/** Chinese display labels for statuses. */
export const MEETING_STATUS_LABELS: Record<MeetingStatus, string> = {
  upcoming: "未开始",
  in_progress: "进行中",
  ended: "已结束",
  cancelled: "已取消",
};

/** Chinese display labels for transcript status. */
export const TRANSCRIPT_STATUS_LABELS: Record<TranscriptStatus, string> = {
  none: "无转录",
  pending: "转录中",
  ready: "已转录",
  failed: "转录失败",
};

/** Selectable source options for filters / forms. */
export const MEETING_SOURCE_OPTIONS: { value: MeetingSource; label: string }[] = (
  Object.entries(MEETING_SOURCE_LABELS) as [MeetingSource, string][]
).map(([value, label]) => ({ value, label }));

/** Selectable status options for filters. */
export const MEETING_STATUS_OPTIONS: { value: MeetingStatus; label: string }[] = (
  Object.entries(MEETING_STATUS_LABELS) as [MeetingStatus, string][]
).map(([value, label]) => ({ value, label }));

/** External platforms that support MCP sync (excludes `manual`). */
export const EXTERNAL_MEETING_SOURCES: MeetingSource[] = [
  "tencent",
  "dingtalk",
  "feishu",
  "zoom",
];

/** A participant entry stored on Meeting.participant_emails JSONB. */
export interface MeetingParticipant {
  email?: string | null;
  name?: string | null;
  attendance?: string | null;
}

/** A meeting record (MeetingResponse). */
export interface Meeting {
  id: string;
  matterId: string | null;
  source: MeetingSource;
  externalMeetingId: string | null;
  title: string;
  status: MeetingStatus;
  startsAt: string | null;
  endsAt: string | null;
  locationOrUrl: string | null;
  organizerEmail: string | null;
  participantEmails: MeetingParticipant[] | null;
  /**
   * 导入态。`listed` 的会议只有清单信息，界面不得呈现纪要相关内容，只给导入入口。
   */
  importState: MeetingImportState;
  transcriptStatus: TranscriptStatus;
  /** 转录来源；决定 pending / failed 时界面给出的重试动作。 */
  transcriptSource: TranscriptSource | null;
  transcriptText: string | null;
  transcriptLang: string | null;
  summary: string | null;
  prepChecklist: Record<string, unknown> | null;
  /** 归档纪要文档标识；非空即表示这场会已归档，入口切成「查看纪要文档」。 */
  minutesDocumentId: string | null;
  archivedAt: string | null;
  archivedById: string | null;
  /** 处理阶段总览标签，供列表筛选与详情头部展示；派生字段，见类型定义注释。 */
  processingStage: MeetingProcessingStage;
  /**
   * 本人确认时刻与确认人。非空即表示这场会的纪要已经过律师本人确认——确认前
   * 纪要与待办均为 AI 草稿，不写日程、不进卷宗、不可分享；归档与待办确认动作
   * 在确认前禁用。个人阶段的确认人即律师本人，无组织审批语义。
   */
  confirmedAt: string | null;
  confirmedById: string | null;
  /** 会前准备提示的产生时刻；非空表示这场会已被选为「可以先准备」。 */
  prepPromptedAt: string | null;
  /** 会后总结的产出状态与留痕。三项状态各自独立。 */
  summaryState: MeetingAnalysisState;
  summarySubmittedAt: string | null;
  summaryGeneratedAt: string | null;
  summaryError: string | null;
  summaryTaskId: string | null;
  /** 待办抽取的产出状态与留痕。 */
  tasksState: MeetingAnalysisState;
  tasksSubmittedAt: string | null;
  tasksExtractedAt: string | null;
  tasksError: string | null;
  tasksTaskId: string | null;
  /** 会前准备的产出状态与留痕。 */
  prepState: MeetingAnalysisState;
  prepSubmittedAt: string | null;
  prepGeneratedAt: string | null;
  prepError: string | null;
  prepTaskId: string | null;
  /** 归属档位（personal / matter / org）与首个创建者、导入者。 */
  ownerScope: string;
  ownerUserId: string | null;
  /**
   * Recorder-pen audio metadata. All nullable — populated when this row is
   * a recorder-pen capture, null for plain external-sync meetings.
   * `audioStorageKey` is the canonical "has audio" flag (non-null ⇔ has audio).
   */
  audioStorageKey: string | null;
  audioFilename: string | null;
  audioMime: string | null;
  audioSizeBytes: number | null;
  audioDurationSeconds: number | null;
  recordedAt: string | null;
  isKeyMeeting: boolean;
  meetingType: MeetingType | null;
  /**
   * ASR task state (Phase 2). `transcriptStatus` is the headline state;
   * these three carry the operational details for the UI:
   * - `transcriptionTaskId`: Celery task id (for log correlation)
   * - `transcriptionProgress`: 0-100, drives the progress bar
   * - `transcriptionError`: surfaced when status === "failed"
   */
  transcriptionTaskId: string | null;
  transcriptionProgress: number | null;
  transcriptionError: string | null;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
}

/** Payload for manual meeting creation (MeetingCreate). */
export interface MeetingCreateRequest {
  matterId?: string;
  source?: MeetingSource;
  title: string;
  startsAt?: string | null;
  endsAt?: string | null;
  locationOrUrl?: string | null;
  organizerEmail?: string | null;
  participantEmails?: MeetingParticipant[] | null;
  /** Recorder-pen fields. Default false. */
  isKeyMeeting?: boolean;
  meetingType?: MeetingType | null;
}

/** Partial update (MeetingUpdate). */
export interface MeetingUpdateRequest {
  title?: string;
  matterId?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  locationOrUrl?: string | null;
  organizerEmail?: string | null;
  participantEmails?: MeetingParticipant[] | null;
  status?: MeetingStatus;
  isKeyMeeting?: boolean;
  meetingType?: MeetingType | null;
}

/** Manual transcript import (MeetingTranscriptImport). */
export interface MeetingTranscriptImportRequest {
  text: string;
  lang?: string | null;
}

/**
 * 逐场导入会议内容（MeetingContentImport）。
 *
 * `platform_minutes` 向该会议所属平台拉取纪要，不需要 `text`；
 * `manual_paste` 由律师粘贴纪要正文，`text` 必填。
 * 录音转写不走这条通路，入口是录音上传与转写。
 */
export interface MeetingContentImportRequest {
  transcriptSource: Extract<TranscriptSource, "platform_minutes" | "manual_paste">;
  text?: string | null;
  lang?: string | null;
}

/** One bullet under key_points (a discussion topic with its detail). */
export interface MeetingKeyPoint {
  topic: string;
  detail: string;
}

/** One resolved decision from the meeting. */
export interface MeetingDecision {
  text: string;
  rationale?: string | null;
}

/** One brief action item extracted from the transcript. */
export interface MeetingActionBrief {
  text: string;
  assignee?: string | null;
  due?: string | null;
}

/** Overall meeting tone, returned by the meeting-summarize skill. */
export type MeetingOverallTone = "positive" | "neutral" | "cautious" | "contentious";

/**
 * Structured summary written to ``Meeting.summary`` as JSON.
 *
 * Backend persists the meeting-summarize skill output as a JSON string in the
 * ``summary`` Text column; frontend parses it back into this shape. Old rows
 * (pre-P2) stored plain text — caller should fall back to a `{summary: text}`
 * shape when JSON parsing fails.
 */
export interface MeetingSummaryPayload {
  summary: string;
  keyPoints: MeetingKeyPoint[];
  decisions: MeetingDecision[];
  actionItemsBrief: MeetingActionBrief[];
  overallTone: MeetingOverallTone;
}

/**
 * 七段式会谈纪要（S11），写入 ``Meeting.summary`` 的新结构，逐步取代
 * {@link MeetingSummaryPayload}。七段对应律所会谈纪要文书的基本纪律：
 * 把「事实」与「意见」分开、把「已决」与「待核实」分开。
 *
 * 除一页摘要外六段均为要点列表；`decisions` 与旧结构同名字段含义不同——
 * 旧结构是 `{text, rationale}[]`，新结构是纯文本要点列表，用
 * {@link isSevenSectionMeetingSummary} 判别，不要按字段名直接类型断言。
 */
export interface MeetingSummaryPayloadV2 {
  /** 一页摘要：面向不看逐字稿的人，一段话说清这场会谈了什么、结果如何。 */
  onePageSummary: string;
  /** 讨论议题。 */
  topics: string[];
  /** 关键事实：客户陈述、对方表态等事实性内容，与「决策和结论」区分开。 */
  keyFacts: string[];
  /** 法律风险：律师在会谈中给出的风险提示。 */
  legalRisks: string[];
  /** 决策和结论：会上已经达成一致的事项。 */
  decisions: string[];
  /** 待核实问题：尚待取证或尽调确认的问题，与「已决」区分开。 */
  openQuestions: string[];
  /** 下一步工作。 */
  nextSteps: string[];
}

/** 七段式纪要各段的标题，渲染顺序即数组顺序。 */
export const MEETING_SUMMARY_V2_SECTIONS: {
  key: keyof Omit<MeetingSummaryPayloadV2, "onePageSummary">;
  label: string;
}[] = [
  { key: "topics", label: "讨论议题" },
  { key: "keyFacts", label: "关键事实" },
  { key: "legalRisks", label: "法律风险" },
  { key: "decisions", label: "决策和结论" },
  { key: "openQuestions", label: "待核实问题" },
  { key: "nextSteps", label: "下一步工作" },
];

/** 判别是否七段式纪要——存在 `onePageSummary` 字段是唯二结构互斥的判据。 */
export function isSevenSectionMeetingSummary(
  payload: MeetingSummaryPayload | MeetingSummaryPayloadV2,
): payload is MeetingSummaryPayloadV2 {
  return "onePageSummary" in payload;
}

/** A Task row created by the meeting-tasks skill (subset of Task response). */
export interface MeetingExtractedTask {
  id: string;
  matterId: string | null;
  meetingId: string | null;
  title: string;
  type: string;
  source: string;
  status: string;
  priority: string;
  assigneeId: string | null;
  assignerId: string | null;
  deadlineAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * 待办抽取就绪后的产出（`state === "ready"` 时的 result）。
 *
 * 抽取只产出候选条目，不产出正式任务：候选不进任务列表，要由律师在确认环节逐条
 * 保留、指派、定截止时间之后才成为正式任务。
 */
export interface MeetingExtractTasksResult {
  candidatesCount: number;
  candidates: MeetingExtractedTask[];
}

/**
 * 一条候选待办。字段与正式任务同形，差别在于 `status === "candidate"`、指派人与
 * 截止时间为空，且 `notes` 里保留了技能识别到的原文（责任人、截止说法、依据）。
 */
export type MeetingTaskCandidate = MeetingExtractedTask;

/** 律师对一条候选待办的确认结果（MeetingTaskCandidateDecision）。 */
export interface MeetingTaskCandidateDecision {
  taskId: string;
  /** false 表示丢弃这一条；true 时 `assigneeId` 必填。 */
  keep: boolean;
  assigneeId?: string | null;
  /** 律师填写的确定截止时间；非必填。 */
  deadlineAt?: string | null;
  priority?: string | null;
}

/** 一次确认提交的结果（整批生效或整批不生效）。 */
export interface MeetingTaskCandidateConfirmResult {
  promoted: number;
  discarded: number;
  tasks: MeetingExtractedTask[];
}

/** One item on the pre-meeting checklist. */
export interface MeetingPrepChecklistItem {
  item: string;
  priority: "high" | "medium" | "low";
  rationale?: string;
}

/**
 * Structured pre-meeting prep result, written to ``Meeting.prepChecklist``.
 *
 * Backend writes the meeting-prep skill output as a JSONB dict (snake_case
 * keys) directly to the column; the API method converts to camelCase.
 */
export interface MeetingPrepPayload {
  clientBrief: string;
  memoPoints: string[];
  checklist: MeetingPrepChecklistItem[];
  overallStrategy: string;
}

/** List endpoint query params. ``sources`` (multi-value OR) supersedes ``source``. */
export interface MeetingListParams {
  source?: MeetingSource;
  sources?: MeetingSource[];
  status?: MeetingStatus;
  /** 处理阶段筛选（左栏快速筛选项），对应 `processing_stage` 查询参数。 */
  processingStage?: MeetingProcessingStage;
  matterId?: string;
  dateFrom?: string;
  dateTo?: string;
  /** Recorder-pen filters. */
  hasAudio?: boolean;
  isKeyMeeting?: boolean;
  meetingType?: MeetingType;
  page?: number;
  pageSize?: number;
}

/** Public view of a user-platform binding (MeetingConnectionResponse). */
export interface MeetingConnection {
  id: string;
  source: MeetingSource;
  status: MeetingConnectionStatus;
  /** 授权通道：oauth（标准应用）/ device（CLI 同款设备授权）。 */
  authMode?: "oauth" | "device";
  expiresAt: string | null;
  externalUserId: string | null;
  scopes: string[] | null;
  createdAt: string;
  updatedAt: string;
}

/** 周期性会议规则（预订时可选）。 */
export interface MeetingBookRecurringRule {
  recurringType: number;
  untilType: number;
  untilCount?: number;
  untilDate?: string;
}

/** 在会议平台上预订会议的请求载荷。 */
export interface MeetingBookPayload {
  source: MeetingSource;
  subject: string;
  startsAt: Date;
  endsAt: Date;
  recurringRule?: MeetingBookRecurringRule;
  password?: string;
}

// ---------------------------------------------------------------------------
// 逐字稿分段（S11：说话人 + 时间戳 + 点击回放）
// ---------------------------------------------------------------------------

/**
 * 一段带时间戳的逐字稿（GET /meetings/{id}/transcript 的 `segments` 项）。
 *
 * 仅当纪要来自带时间戳的转写（faster-whisper 本地 ASR 或平台纪要）时存在；
 * 手工粘贴的纪要没有分段，界面回退到纯文本渲染。
 */
export interface MeetingTranscriptSegment {
  index: number;
  startMs: number;
  endMs: number;
  /** 说话人标签；说话人分离未启用时为「说话人 1/2」占位，可手工改名。 */
  speaker: string | null;
  text: string;
  highlight: boolean;
}

/** GET /meetings/{id}/transcript 的响应体（含 ASR 轮询字段与分段）。 */
export interface MeetingTranscriptResult {
  status: string;
  text: string | null;
  progress: number | null;
  errorMessage: string | null;
  taskId: string | null;
  lang: string | null;
  /** 分段列表；无分段（手工粘贴纪要）时为 null，界面回退到纯文本渲染。 */
  segments: MeetingTranscriptSegment[] | null;
}

/** 说话人批量改名（PATCH /meetings/{id}/transcript/speakers）。 */
export interface MeetingTranscriptSpeakerRenameRequest {
  from: string;
  to: string;
}

/** 单段逐字稿的可编辑字段——按需传，未传的字段不改。 */
export interface MeetingTranscriptSegmentPatch {
  index: number;
  text?: string;
  speaker?: string;
  highlight?: boolean;
}

/** 逐字稿分段编辑（PATCH /meetings/{id}/transcript/segments）。 */
export interface MeetingTranscriptSegmentsEditRequest {
  segments: MeetingTranscriptSegmentPatch[];
}
